<template>
  <v-container
    fluid
    class="account-deletion-page tw-min-h-screen tw-flex tw-items-center tw-justify-center"
  >
    <v-card
      max-width="500"
      width="100%"
      class="mx-auto pa-8 pa-sm-10"
      rounded="lg"
      elevation="4"
    >
      <!-- Logo -->
      <div class="text-center mb-6">
        <img
          v-if="logoUrl"
          :src="logoUrl"
          :alt="$t('common.logo')"
          style="max-height: 56px; max-width: 200px; object-fit: contain;"
        />
        <v-icon v-else size="56" color="primary">mdi-shield-account</v-icon>
      </div>

      <!-- Title -->
      <h1 class="text-h5 text-center font-weight-bold mb-6" style="color: #333;">
        {{ $t('accounts.deleteAccount') }}
      </h1>

      <!-- Form state -->
      <template v-if="!submitted">
        <!-- Warning -->
        <v-alert
          type="warning"
          text
          dense
          class="mb-5"
          border="left"
        >
          <div class="body-2">
            {{ $t('accounts.requestingAccountDeletionWillPermanentlyRemove') }}
          </div>
        </v-alert>

        <v-form ref="form" v-model="valid" @submit.prevent="submitRequest">
          <!-- Phone number -->
          <v-text-field
            v-model="phone"
            :label="$t('common.phoneNumber')"
            placeholder="e.g. 96512345678"
            prepend-inner-icon="mdi-phone"
            outlined
            :rules="phoneRules"
            type="tel"
            class="mb-1"
          ></v-text-field>

          <!-- Password -->
          <v-text-field
            v-model="password"
            :label="$t('accounts.password')"
            prepend-inner-icon="mdi-lock"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showPassword ? 'text' : 'password'"
            outlined
            :rules="passwordRules"
            class="mb-1"
            @click:append="showPassword = !showPassword"
          ></v-text-field>

          <!-- Confirmation -->
          <v-checkbox
            v-model="confirmed"
            :label="$t('accounts.iConfirmThatIWantTo')"
            :rules="[v => !!v || $t('accounts.youMustConfirmToProceed')]"
            color="error"
            class="mt-0"
          ></v-checkbox>

          <!-- Submit -->
          <v-btn
            block
            color="error"
            :disabled="!valid || !confirmed"
            :loading="loading"
            large
            class="mt-2"
            @click="submitRequest"
          >
            <v-icon left>mdi-delete-outline</v-icon>
            {{ $t('accounts.deleteMyAccount') }}
          </v-btn>
        </v-form>

        <!-- Error alert -->
        <v-alert
          v-if="error"
          type="error"
          text
          dense
          class="mt-5"
        >
          {{ error }}
        </v-alert>
      </template>

      <!-- Success state -->
      <div v-else class="text-center py-4">
        <v-icon size="64" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
        <h2 class="text-h6 font-weight-bold mb-3" style="color: #333;">
          {{ $t('accounts.requestSubmitted') }}
        </h2>
        <p class="body-1" style="color: #666;">
          {{ successMessage }}
        </p>
      </div>
    </v-card>
  </v-container>
</template>

<script>
export default {
  data() { return ({
    phone: "",
    password: "",
    showPassword: false,
    confirmed: false,
    valid: false,
    loading: false,
    submitted: false,
    error: "",
    successMessage: "",
    logoUrl: null,
    phoneRules: [
      v => !!v || this.$t('accounts.phoneNumberIsRequired'),
      v => /^\d{8,15}$/.test(v) || this.$t('accounts.enterAValidPhoneNumberDigits'),
    ],
    passwordRules: [
      v => !!v || this.$t('accounts.passwordIsRequired'),
    ],
  }); },
  created() {
    // Pick up logo from brand settings if already loaded by App.vue
    const brand = window.__brandSettings;
    if (brand && brand.logo && brand.logo.horizontal) {
      const src = brand.logo.horizontal;
      if (/^https?:\/\//.test(src)) {
        this.logoUrl = src;
      } else {
        const base = (process.env.VUE_APP_BASE_URL || "").replace(/\/+$/, "");
        this.logoUrl = base ? `${base}/${src.replace(/^\/+/, "")}` : src;
      }
    }
  },
  methods: {
    async submitRequest() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      this.error = "";

      try {
        const apiBase = (process.env.VUE_APP_GRAPHQL_HTTP || "http://localhost:8003/graphql")
          .replace(/\/graphql\/?$/, "");

        // Don't set x-tenant-id manually — the fetch interceptor injects it
        const response = await fetch(`${apiBase}/account/delete-request`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phone: this.phone, password: this.password }),
        });

        const data = await response.json();

        if (data.success) {
          this.submitted = true;
          this.successMessage = data.message;
        } else {
          this.error = data.message || this.$t('accounts.somethingWentWrongPleaseTryAgain');
        }
      } catch (err) {
        console.error("Account deletion request error:", err);
        this.error = this.$t('accounts.unableToConnectToTheServer');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.account-deletion-page {
  background-color: #f8fbfb;
  min-height: 100vh;
}
</style>
